import { toast } from 'sonner';

import { clearTokens } from '../api/auth';

type NavigateFunction = (path: string, options?: { replace?: boolean }) => void;

// Store the navigate function globally
let navigate: NavigateFunction | null = null;

/**
 * Set the navigate function for auth expiry handling
 * This should be called from the main App component or router setup
 */
export function setNavigateFunction(navigateFn: NavigateFunction): void {
  navigate = navigateFn;
}

/**
 * Clear all user data from localStorage
 * This includes auth tokens and any other user-specific data
 */
function clearAllUserData(): void {
  // Clear auth tokens
  clearTokens();
  
  // Clear any other user-specific data from localStorage
  // Add more keys here if your app stores other user-specific data
  const userDataKeys = [
    // Add any additional localStorage keys that contain user data
    // For example: 'user-preferences', 'cart-items', 'saved-searches', etc.
  ];
  
  userDataKeys.forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Clear sessionStorage as well if you use it for user data
  sessionStorage.clear();
}

/**
 * Force logout the user and redirect to login page
 * This is called when auth token is expired/invalid and refresh fails
 * 
 * @param message - Optional custom message to show to user
 * @param silent - If true, won't show toast notification
 */
export function forceLogout(message?: string, silent = false): void {
  // Clear all user data
  clearAllUserData();
  
  // Show notification unless silent
  if (!silent) {
    const logoutMessage = message || 'Your session has expired. Please login again.';
    toast.error(logoutMessage, {
      duration: 5000,
      important: true,
    });
  }
  
  // Navigate to login page
  if (navigate) {
    navigate('/login', { replace: true });
  } else {
    // Fallback if navigate function is not set
    console.warn('Navigate function not set for auth expiry handler. Using window.location.');
    window.location.href = '/login';
  }
}

/**
 * Handle auth token expiry/invalid scenarios
 * This function should be called whenever:
 * - API returns 401 and token refresh fails
 * - Token is detected as invalid
 * - Any auth-related error occurs
 * 
 * @param context - Context where the auth expiry occurred (for debugging)
 * @param error - The error that occurred (optional)
 */
export function handleAuthExpiry(context: string, error?: Error): void {
  console.warn(`Auth token expired/invalid in context: ${context}`, error);
  
  // Force logout with appropriate message
  forceLogout('Your session has expired. Please login again.');
}

/**
 * Check if current route is a public route that doesn't require auth
 */
export function isPublicRoute(pathname: string): boolean {
  const publicRoutes = ['/', '/login', '/privacy-policy', '/terms-and-conditions'];
  return publicRoutes.includes(pathname) || pathname.startsWith('/login');
}

/**
 * Handle auth errors with context-aware messaging
 */
export function handleAuthError(error: any, context: string): void {
  console.error(`Auth error in ${context}:`, error);
  
  // Different handling based on error type
  if (error?.response?.status === 401) {
    handleAuthExpiry(context, error);
  } else if (error?.message?.includes('token')) {
    handleAuthExpiry(context, error);
  } else {
    // Other auth-related errors
    toast.error('Authentication error occurred. Please try logging in again.');
    forceLogout();
  }
}

/**
 * Custom hook to handle API errors with auth expiry checking
 * Use this in components that make API calls
 */
export function useAuthErrorHandler() {
  return {
    handleError: (error: any, context: string) => {
      if (error?.response?.status === 401 || error?.code === 401) {
        handleAuthExpiry(context, error);
      } else {
        // Let component handle non-auth errors
        throw error;
      }
    },
    forceLogout: (message?: string) => forceLogout(message),
  };
}

/**
 * Utility to check if an error is auth-related
 */
export function isAuthError(error: any): boolean {
  return (
    error?.response?.status === 401 ||
    error?.code === 401 ||
    error?.message?.toLowerCase().includes('unauthorized') ||
    error?.message?.toLowerCase().includes('token') ||
    error?.message?.toLowerCase().includes('authentication')
  );
}